import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Article } from '../../../../models/article.model';
import { AuthService } from '../../../../services/auth.service';
import * as articleActions from '../../../../state/actions/article.actions';
import { AppState } from '../../../../state/appstate';
import * as articleSelector from '../../../../state/selectors/article.selector';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';

@Component({
  selector: 'app-edit-article',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadingDotsComponent,
    RouterLink,
  ],
  templateUrl: './edit-article.component.html',
  styleUrls: ['./edit-article.component.css'],
})
export class EditArticleComponent {
  pageForm: FormGroup;

  id: string | null = null;

  article: Article | undefined;
  tags: Array<string> = [];

  articleStateSub: Subscription | undefined;
  working: boolean = false;

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private route: ActivatedRoute
  ) {
    this.id = this.route.snapshot.paramMap.get('id');

    this.pageForm = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(200),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]),
      tagField: new FormControl(''),
    });
    this.store
      .pipe(select(articleSelector.getArticleById(this.id!)))
      .subscribe((article) => {
        if (article != null) {
          this.article = article;
          this.pageForm.patchValue({
            title: article.title,
            description: article.description,
            tagField: '',
          });
          this.tags = article.tags ?? [];
        }
      });
    this.articleStateSub = this.store
      .select((state) => state.articles)
      .subscribe((articleState) => {
        this.working = articleState.inProgress;
      });
  }

  ngOnDestroy(): void {
    this.articleStateSub?.unsubscribe();
  }

  addTag() {
    this.pageForm.get('tagField')?.setErrors(null);
    if (this.pageForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.pageForm.get('tagField')?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' ')) {
      return this.pageForm.controls['tagField'].setErrors({
        invalidValue: true,
      });
    }
    if ((this.tags.length ?? 0) >= 10)
      return this.pageForm.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    this.tags = [...this.tags, fieldValue];
    this.pageForm.get('tagField')?.setValue('');
    this.pageForm.get('tagField')?.markAsPristine();
  }

  removeTag(tag: number) {
    this.tags.splice(tag, 1);
  }

  update() {
    // console.log('EditArticleComponent :: update :: Invoked...');
    if (this.pageForm.invalid) return;

    // console.log('EditArticleComponent :: update :: Starting...');

    return this.store.dispatch(
      articleActions.update({
        article: {
          id: this.article!.id!,
          title: this.pageForm.controls['title'].value.trim(),
          description: this.pageForm.controls['description'].value.trim(),
          tags: this.tags,
        } as Partial<Article>,
      })
    );
  }

  delete() {
    this.store.dispatch(articleActions.deleteAction({ id: this.article!.id! }));
  }
}
